import { RECAPTCHA_SCRIPT_ID } from '@/constants/common';

/**
 * Third-party error noise, filtered out of BOTH error reporters — PostHog
 * and Faro/Grafana.
 *
 * Sharing the array is the point: a new entry silences the error in both, or
 * in neither, so the two dashboards cannot drift apart.
 *
 * Everything here is thrown by scripts we do not ship and cannot patch — most
 * of them injected at runtime by the CCM19 consent manager once a visitor
 * accepts marketing cookies. Nothing in this list is actionable, and none of it
 * originates in our own code.
 */
export const IGNORED_ERROR_MESSAGES = [
  // Facebook Pixel — blocked by ad-blockers / async load races
  "Can't find variable: fbq",
  'fbq is not defined',
  // TikTok pixel — reorders its own globals during load
  '_wct.generateClickID is not a function',
  // Upstream validator rejecting bad input, not a bug
  'BadRequestException: url must be a URL address',
  // Anonymous users hitting protected endpoints
  'UnauthorizedException',
  'Unauthorized',
  // Cross-origin script throwing behind the browser's same-origin curtain. The
  // browser deliberately withholds the message, file and line, so the event is
  // the literal string "Script error." with an empty stack — there is nothing
  // to act on. Recovering the detail needs `crossorigin="anonymous"` on the
  // offending tag AND `Access-Control-Allow-Origin` from its host, which is not
  // available for tags CCM19 injects.
  'Script error.',
  // Android WebView JS-bridge teardown, seen when the page is opened inside an
  // in-app browser (Facebook/Instagram) and the host activity disposes the
  // bridge mid-call. Thrown by the WebView, not by anything we ship, and not
  // fixable from web code.
  'Error invoking postMessage: Java exception was raised during method invocation',
  'Error invoking postMessage: Java object is gone',
  // The iOS half of the same story: the Instagram/Meta in-app browser injects a
  // script into the document that calls its native bridge, and the bridge is
  // absent on the frames where it runs. Every frame in the stack carries our
  // own origin because the script is injected inline, so the message is the
  // only thing identifying it as foreign.
  'window.webkit.messageHandlers',
  'window.ethereum',
  // Same in-app browsers, synthetic and stackless — the injected shim rejecting
  // its own call. Nothing on our side is involved. A numeric code prefixes the
  // message (e.g. `454:`) and varies between throws, so match only the stable
  // text.
  'Handling is disabled',
  // The Meta iOS in-app browser's injected `sendMessageToIFrames` walks every
  // `<iframe>` and posts to its `contentWindow`. On a frame that is lazy,
  // detached, or still mounting — our Trustpilot embeds load lazily —
  // `contentWindow` is null and Safari throws. Match the iframe-walk operation,
  // not the bare `postMessage`, so a genuine iframe failure still reports.
  'contentWindow.postMessage',
  // The Awin affiliate tag's fetch, refused by ITP or an ad-blocker. Every
  // browser names the host it could not reach but disagrees on the verb —
  // Safari says "Load failed", Firefox "NetworkError when attempting to fetch
  // resource." — so this matches the host alone, which is what keeps it from
  // matching a failed fetch of our own API.
  '(www.wepowerconnections.com)',
  // The TikTok pixel's own CDN host, same story as the Awin tag above: the
  // fetch is refused by ITP or an ad-blocker and each browser words it
  // differently, so match the host. Pairs with the `_wct.generateClickID`
  // entry above — same vendor, load failure instead of a load race.
  '(wct-2.com)',
  '(ihrhelden-gtm-server-1034119719660.europe-west3.run.app)',
  // Microsoft's Outlook/Teams link-preview crawler, which opens the page in a
  // hidden frame and drives its own injected script. The message is that
  // script's internal bookkeeping (`MethodName`/`ParamCount` are not our
  // vocabulary), it arrives as a non-Error rejection with no stack, and no
  // human is on the other end of it.
  'Object Not Found Matching Id:',
  // CCM19 again: its own bundle keeps a handle to the iframe it replaced
  // (`ccm19OriginalIframe`) and reads `.contentWindow.location` off it after
  // the frame is detached. Vendor property name, vendor bug — nothing under
  // `src/` knows this object.
  'ccm19OriginalIframe',
  // The WCT (wepowerconnections) affiliate tag reporting its own backend down,
  // routed through `console.error` by the vendor script. Their outage, their
  // endpoint — nothing on our side to fix, and it arrives in bursts whenever
  // the tag's server has a bad day.
  'WCT Server Unavailable',
  // A DOM `Event` thrown or rejected instead of an Error — the reporters wrap
  // it as "Non-Error ... with keys: isTrusted", `isTrusted` being the giveaway
  // that it is an Event object. Comes from third-party scripts rejecting with
  // the raw event of a failed load. Matched on the `isTrusted` suffix only:
  // the bare "Non-Error exception captured with keys:" prefix would also
  // swallow a genuine non-Error throw from our own code, which we do want.
  'Non-Error exception captured with keys: isTrusted',
  // A browser extension's own cross-frame message bus timing out — the frame
  // carries no chunk id, so it is not part of our bundle. Seen across
  // unrelated pages/sessions, which rules out anything page-specific of ours.
  'Message Timeout',
  'No Listener: tabs:',
  '.store.cashback.supported',
  // A `fetch` the browser itself aborted (navigation away mid-request), not a
  // throw from our code — PostHog reports it stackless and `synthetic: true`.
  // Full match: `AbortError` is also a real DOMException name our own code
  // could throw deliberately, so only this exact wrapper text is noise.
  "'AbortError' captured as exception with message: 'Aborted'",
  // The same navigation-away abort as Chrome and Safari word it when the
  // request is cancelled without a reason. Our own timeouts use
  // `AbortSignal.timeout`, which throws `TimeoutError`, not this.
  'AbortError: The user aborted a request.',
  // The standard Chromium/Firefox warning for a ResizeObserver callback that
  // couldn't finish delivering notifications within one frame. Not an error —
  // every major framework hits this at some point, and it self-recovers.
  'ResizeObserver loop completed with undelivered notifications.',
  // CCM19 consent manager (`/app.js`) iterating its own collection and calling
  // `indexOf` on an `.id` that is not a string. Thrown inside the vendor bundle,
  // which we do not ship — no code under `src/` reads an `.id` and calls
  // `indexOf` on it. The `.id.indexOf` prefix keeps this from matching a bare
  // `indexOf is not a function` we might throw ourselves later. It surfaced only
  // once `crossorigin="anonymous"` let the real message through the "Script
  // error." curtain.
  '.id.indexOf is not a function',
  // A session-replay recorder (rrweb) walks every iframe on the page and reads a
  // property off each frame's `contentWindow`. On our cross-origin frames
  // (Trustpilot, the tariff calculator, reCAPTCHA, the tag manager) Firefox
  // throws where Chrome returns null. The property name in the message varies
  // per frame, so this matches on the fixed Firefox wording that precedes it.
  'Permission denied to access property',
  // A parse-time SyntaxError with zero stack frames — impossible from our own
  // bundle, since a real syntax error there fails the build, not one visitor's
  // runtime. Seen once, isolated to a single session in a few seconds, the
  // signature of a script truncated mid-download by a proxy/ad-blocker/AV on
  // that one connection.
  "Unexpected token 'else'",
  'signal is aborted without reason',
  'Could not establish connection. Receiving end does not exist.',
  "Can't find variable: _AutofillCallbackHandler",
  // A shopping/coupon autofill extension logging its own refusal to touch the
  // checkout page (`checkoutUrl [object Object]` — it stringifies an object
  // into its own message). Nothing under `src/` references the plugin. Matched
  // on the plugin prefix so its other messages are covered too.
  '[AutofillPlugin]',
  // Firefox for iOS injects `window.__firefox__` (reader mode, YouTube quality
  // refresh) into every page and its own scripts run before it exists. Covers
  // both the ReferenceError and the `window.__firefox__.<fn>` TypeErrors.
  '__firefox__',
  // A third-party script's XHR refused by the network or an ad-blocker. We use
  // `fetch` throughout, so a failed XHR `send` is never ours.
  "Failed to execute 'send' on 'XMLHttpRequest'",
  'PostHog request timed out',
  ': Java object is gone',
  'Invalid call to runtime.sendMessage(). Tab not found.',
  '["@context"].toLowerCase',
  // Faro's own beacon to the collector failing (offline, ad-blocker, extension
  // fetch wrapper). It reports the failure of the very pipe that would carry it.
  'Failed to fetch (grafana.ihrhelden.de)',
  // Next's client router could not fetch an RSC payload (flaky mobile network)
  // and already falls back to a full browser navigation, so the visitor is fine.
  'Failed to fetch RSC payload for',
  // In-app browsers (Google app, Meta) evaluate their own script in the page and
  // reference globals they never defined: `xbrowser`, `swbrowser`, and
  // `window._META.IABInnerFrame`. None of these names exists under `src/`.
  'xbrowser is not defined',
  'swbrowser is not defined',
  'IABInnerFrame',
  // Reads a `M_ID` key off an undefined vendor object; no code under `src/`
  // knows that name.
  "reading 'M_ID'",
  // Parse-time SyntaxErrors: a syntax error in our own bundle fails the build, so
  // these are scripts truncated or rewritten in transit on one visitor's
  // connection (proxy, ad-blocker, in-app browser). Same reasoning as the
  // `Unexpected token 'else'` entry above.
  "Unexpected identifier 'https'",
  'Unexpected end of input'
] as const;

/**
 * Whole-message matches, for text too short to match as a substring.
 *
 * `Load failed` is Safari's uncaught `fetch` rejection with no host and no stack.
 * Every fetch we make catches its own failure (RTK Query's `fetchBaseQuery`, the
 * `fetch` helpers under `src/lib`), so an uncaught one is a third-party tag's
 * beacon refused by an ad-blocker or a dropped connection. Newer Safari appends the
 * host (`Load failed (api.example.com)`); that form does not match here, so a
 * failure that names our API still reports.
 */
const IGNORED_EXACT_ERROR_MESSAGES: readonly string[] = [
  'Load failed',
  'TypeError: Load failed'
];

/**
 * Substring match, matching how both reporters treat their own ignore lists so one
 * list behaves identically in both. Browsers word the same failure
 * differently — Safari reports `_wct.generateClickID is not a function. (In
 * '_wct.generateClickID()', ...)` where Chrome stops at `is not a function` —
 * so matching on substrings catches both without an entry per browser.
 */
export const isIgnoredErrorMessage = (message: unknown): boolean =>
  typeof message === 'string' &&
  (IGNORED_EXACT_ERROR_MESSAGES.includes(message) ||
    IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored)));

/**
 * Console lines Faro reports as logs that are safe to drop. Deliberately NOT
 * `IGNORED_ERROR_MESSAGES`: that list has broad entries (`Unauthorized`,
 * `Script error.`, ...) that would also swallow our own `console.error` lines.
 * Only entries that name a third party belong here.
 */
export const IGNORED_LOG_MESSAGES = ['[AutofillPlugin]'] as const;

export const isIgnoredLogMessage = (message: unknown): boolean =>
  typeof message === 'string' &&
  IGNORED_LOG_MESSAGES.some((ignored) => message.includes(ignored));

const RECAPTCHA_MISSING_SITEKEY = 'Missing required parameters: sitekey';

/**
 * reCAPTCHA teardown race on /contact. Leaving the page unmounts
 * `GoogleReCaptchaProvider`, whose cleanup clears `window.___grecaptcha_cfg`
 * and removes its script tag — but a gstatic `recaptcha__*.js` that already
 * started downloading still runs, finds no config and throws this. Keyed on the
 * provider's script tag being gone, not on the message alone: while the tag is
 * still there, the same message means reCAPTCHA really ran without a key on a
 * live form, and that must keep reporting.
 */
export const isRecaptchaTeardownError = (message: unknown): boolean =>
  typeof message === 'string' &&
  message.includes(RECAPTCHA_MISSING_SITEKEY) &&
  typeof document !== 'undefined' &&
  !document.getElementById(RECAPTCHA_SCRIPT_ID);

const SERVICE_WORKER_REGISTER_FAILED = 'Failed to register a ServiceWorker';
const UNAUTHORIZED_RESPONSE = '(401)';

export const isProtectedHostServiceWorkerError = (
  message: unknown,
  appEnv: string | undefined
): boolean =>
  appEnv !== 'production' &&
  typeof message === 'string' &&
  message.includes(SERVICE_WORKER_REGISTER_FAILED) &&
  message.includes(UNAUTHORIZED_RESPONSE);

/**
 * URL scheme Safari stamps onto a browser extension's content-script frames.
 * The real script URL is hidden, so a throw whose every frame carries this
 * scheme is extension code, not code we serve. Unlike the list above, the rule
 * keys on the frame filename, not the message: the message is a mangled name
 * (`t.filter`) that changes on each extension rebuild and could collide with a
 * real first-party error, so it is not safe to add to `IGNORED_ERROR_MESSAGES`.
 */
export const MASKED_FRAME_URL_PREFIX = 'webkit-masked-url://';

interface StackFrameLike {
  filename?: string | null;
}

/**
 * True when every stack frame is a Safari-masked extension frame — and there is
 * at least one frame to judge. Both reporters name the frame's file `filename`,
 * so one predicate serves PostHog's `$exception_list` and Faro's
 * exception payload alike.
 *
 * A stackless event returns false, so it still falls through to the
 * message-based rules; a single unmasked frame (our own code sharing the throw)
 * keeps the event. Frames is guarded for non-array input, which the reporters
 * can emit for a malformed stacktrace.
 */
export const areAllFramesMasked = (
  frames: readonly (StackFrameLike | null | undefined)[] | null | undefined
): boolean =>
  Array.isArray(frames) &&
  frames.length > 0 &&
  frames.every((frame) =>
    Boolean(frame?.filename?.startsWith(MASKED_FRAME_URL_PREFIX))
  );

const ANONYMOUS_FRAME_FILENAME = '<anonymous>';

/**
 * True when at least one frame comes from `prefix` and every other frame is
 * either from `prefix` too or an `<anonymous>` callback frame. Anonymous frames
 * are neutral: a vendor script calls them, but they do not identify any file.
 */
const areAllFramesFrom =
  (prefix: string) =>
  (
    frames: readonly (StackFrameLike | null | undefined)[] | null | undefined
  ): boolean => {
    const isFromPrefix = (frame: StackFrameLike | null | undefined) =>
      Boolean(frame?.filename?.startsWith(prefix));

    return (
      Array.isArray(frames) &&
      frames.some(isFromPrefix) &&
      frames.every(
        (frame) =>
          isFromPrefix(frame) || frame?.filename === ANONYMOUS_FRAME_FILENAME
      )
    );
  };

export const areAllFramesFromCcm19 = areAllFramesFrom(
  'https://cloud.ccm19.de/'
);

/**
 * The TikTok pixel lazy-loads its own chunks and resolves some of them against
 * our origin, where they do not exist. The result is a `ChunkLoadError` that
 * only the pixel bundle throws. Match on the frame host, not the message: a
 * `ChunkLoadError` message rule would also hide real stale-chunk errors.
 */
export const areAllFramesFromTikTokPixel = areAllFramesFrom(
  'https://analytics.tiktok.com/'
);

/**
 * In-app browsers that evaluate their own script inside the page: TikTok
 * (`musical_ly`, `BytedanceWebview`), the Google app (`GSA/`), Facebook
 * (`FBAN`, `FBAV`, `FB_IAB`) and Instagram. Plain Mobile Safari and Chrome do
 * not match.
 */
const IN_APP_BROWSER_USER_AGENT =
  /musical_ly|BytedanceWebview|TikTok|GSA\/|FBAN|FBAV|FB_IAB|Instagram/i;

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

const isNextChunk = (url: string) => {
  try {
    return new URL(url).pathname.startsWith('/_next/');
  } catch {
    return false;
  }
};

/**
 * An in-app browser runs its injected script as if it were inline in the page,
 * so every frame of the throw points at a document on our origin
 * (`https://host/what-is-ihrHelden:230:409`) and never at a `_next` chunk. The
 * message varies (a numeric `undefined` read in TikTok, `Maximum call stack size
 * exceeded` in the Google app, `InvalidAccessError` from the Instagram bridge's
 * `postMessage`), so the rule keys on the user agent and the frames, not on the
 * message.
 *
 * The document frame can name an earlier page than the current one: the script
 * was injected on the landing page and client navigation has moved the URL on
 * since. Frames without a file name are native calls such as `postMessage` and
 * say nothing about who called them, so they are skipped. Our own code ships as
 * `_next` chunks, so one chunk frame keeps the event, as does a frame from
 * another origin and the same stack in an ordinary browser.
 *
 * This holds only while we run no script of our own inline in the page: GTM and
 * CCM19 load from their own origins, JSON-LD does not execute, and partner
 * widgets run in a sandboxed `srcDoc` frame. An inline `<script>` or `<Script>`
 * without `src` would hide its own errors from these in-app visitors (most paid
 * Meta traffic), so ship it as a `_next` module instead.
 */
export const isInAppInjectedError = ({
  userAgent,
  pageUrl,
  frames
}: {
  userAgent: string | undefined;
  pageUrl: string | undefined;
  frames: readonly (StackFrameLike | null | undefined)[] | null | undefined;
}): boolean => {
  if (!userAgent || !IN_APP_BROWSER_USER_AGENT.test(userAgent)) return false;
  const pageOrigin = pageUrl ? originOf(pageUrl) : null;
  if (!pageOrigin || !Array.isArray(frames)) return false;
  const filenames = frames
    .map((frame) => frame?.filename)
    .filter((filename): filename is string => Boolean(filename));
  return (
    filenames.length > 0 &&
    filenames.every(
      (filename) => originOf(filename) === pageOrigin && !isNextChunk(filename)
    )
  );
};

export const isAutomatedBrowser = (): boolean =>
  typeof navigator !== 'undefined' && navigator.webdriver === true;
